/**
 * <about-timeline>
 *
 * Horizontal scroll-snap rail on desktop, vertical stack on mobile — one DOM,
 * layout switched entirely in CSS. This element adds:
 *   - pointer drag (mouse + touch + pen, via Pointer Events)
 *   - an in-view milestone indicator driven by IntersectionObserver
 *   - keyboard navigation for the scroll region
 *   - prev/next controls
 *   - Shopify theme editor integration
 *
 * Replaces the previous inline implementation, which was mouse-only, derived
 * its year readout by interpolating months across the scroll percentage rather
 * than reading the milestones, and positioned every item at a hardcoded pixel
 * offset.
 */
(function () {
  'use strict';

  var DESKTOP_QUERY = '(min-width: 750px)';
  var DRAG_THRESHOLD = 4; // px of travel before a press counts as a drag

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  class AboutTimeline extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-atl-rail]');
      if (!this.rail) return;

      this.items = Array.prototype.slice.call(this.querySelectorAll('[data-atl-item]'));
      if (!this.items.length) return;

      this.yearEl = this.querySelector('[data-atl-year]');
      this.dateEl = this.querySelector('[data-atl-date]');
      this.prevBtn = this.querySelector('[data-atl-prev]');
      this.nextBtn = this.querySelector('[data-atl-next]');
      this.progressBar = this.querySelector('[data-atl-progress]');

      this.controller = new AbortController();
      this.signal = this.controller.signal;

      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
      this.isHorizontal = this.railIsHorizontal();

      this.activeItem = null;
      this.pointerId = null;
      this.dragging = false;
      this.dragIntent = false;
      this.scrollFrame = null;

      this.bindScrollRegion();
      this.bindControls();
      this.bindBreakpoint();
      this.bindThemeEditor();
      this.observeItems();

      this.updateTabIndex();
      this.updateControls();

      // Seed the indicator so it is never blank before the first scroll.
      this.setActiveItem(this.items[0]);
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      if (this.observer) this.observer.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    }

    /* ----------------------------------------------------------------------
       Layout state
       ---------------------------------------------------------------------- */

    /**
     * True when the rail is a horizontal scroll container. On mobile the
     * default layout is a vertical stack with no scroll rail, so drag,
     * snapping and the prev/next controls must all stand down.
     */
    railIsHorizontal() {
      if (this.desktopQuery.matches) return true;
      return this.dataset.mobileLayout === 'scroll';
    }

    updateTabIndex() {
      // A horizontally scrolling region needs to be focusable to be reachable
      // by keyboard. A static stack does not, and would just be a dead stop.
      this.rail.setAttribute('tabindex', this.isHorizontal ? '0' : '-1');
    }

    /* ----------------------------------------------------------------------
       Scroll region: drag + keyboard + scroll tracking
       ---------------------------------------------------------------------- */

    bindScrollRegion() {
      var opts = { signal: this.signal };

      this.rail.addEventListener('pointerdown', this.onPointerDown.bind(this), opts);
      this.rail.addEventListener('pointermove', this.onPointerMove.bind(this), opts);
      this.rail.addEventListener('pointerup', this.onPointerUp.bind(this), opts);
      this.rail.addEventListener('pointercancel', this.onPointerUp.bind(this), opts);
      this.rail.addEventListener('keydown', this.onKeyDown.bind(this), opts);
      this.rail.addEventListener('scroll', this.onScroll.bind(this), { signal: this.signal, passive: true });

      // Suppress the click that follows a drag so a drag ending on a link
      // does not navigate.
      this.rail.addEventListener(
        'click',
        function (event) {
          if (this.dragIntent) {
            event.preventDefault();
            event.stopPropagation();
            this.dragIntent = false;
          }
        }.bind(this),
        { signal: this.signal, capture: true }
      );
    }

    onPointerDown(event) {
      if (!this.isHorizontal) return;
      // Let the browser own touch panning; hijacking it costs momentum
      // scrolling and feels worse than the native behaviour.
      if (event.pointerType === 'touch') return;
      if (event.button !== 0) return;

      this.pointerId = event.pointerId;
      this.dragging = true;
      this.dragIntent = false;
      this.dragStartX = event.clientX;
      this.dragStartScroll = this.rail.scrollLeft;
    }

    onPointerMove(event) {
      if (!this.dragging || event.pointerId !== this.pointerId) return;

      var walk = event.clientX - this.dragStartX;

      if (!this.dragIntent) {
        if (Math.abs(walk) < DRAG_THRESHOLD) return;
        // Only now is this a drag rather than a click. Capture the pointer so
        // movement outside the rail keeps tracking, and disable snapping so
        // the rail does not fight the drag.
        this.dragIntent = true;
        this.classList.add('is-dragging');
        try {
          this.rail.setPointerCapture(event.pointerId);
        } catch (e) {
          /* setPointerCapture can throw if the pointer is already gone */
        }
      }

      event.preventDefault();
      this.rail.scrollLeft = this.dragStartScroll - walk;
    }

    onPointerUp(event) {
      if (!this.dragging || (this.pointerId !== null && event.pointerId !== this.pointerId)) return;

      if (this.rail.hasPointerCapture && this.rail.hasPointerCapture(event.pointerId)) {
        this.rail.releasePointerCapture(event.pointerId);
      }

      this.dragging = false;
      this.pointerId = null;
      this.classList.remove('is-dragging');

      // dragIntent is cleared by the capture-phase click handler, or here if
      // no click follows.
      if (this.dragIntent) {
        window.setTimeout(
          function () {
            this.dragIntent = false;
          }.bind(this),
          0
        );
      }
    }

    onKeyDown(event) {
      if (!this.isHorizontal) return;

      var index = this.items.indexOf(this.activeItem);
      if (index === -1) index = 0;

      switch (event.key) {
        case 'ArrowRight':
          this.scrollToIndex(index + 1);
          break;
        case 'ArrowLeft':
          this.scrollToIndex(index - 1);
          break;
        case 'Home':
          this.scrollToIndex(0);
          break;
        case 'End':
          this.scrollToIndex(this.items.length - 1);
          break;
        default:
          return;
      }

      event.preventDefault();
    }

    onScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(
        function () {
          this.scrollFrame = null;
          this.updateControls();
        }.bind(this)
      );
    }

    /* ----------------------------------------------------------------------
       Navigation
       ---------------------------------------------------------------------- */

    bindControls() {
      var opts = { signal: this.signal };

      if (this.prevBtn) {
        this.prevBtn.addEventListener(
          'click',
          function () {
            this.step(-1);
          }.bind(this),
          opts
        );
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener(
          'click',
          function () {
            this.step(1);
          }.bind(this),
          opts
        );
      }
    }

    step(direction) {
      var index = this.items.indexOf(this.activeItem);
      if (index === -1) index = 0;
      this.scrollToIndex(index + direction);
    }

    scrollToIndex(index) {
      var item = this.items[Math.max(0, Math.min(index, this.items.length - 1))];
      if (item) this.scrollToItem(item);
    }

    /**
     * Centre an item in the rail. Computed from bounding rects rather than
     * offsetLeft so it does not depend on which ancestor happens to be the
     * offset parent, and used in place of scrollIntoView() so the page itself
     * never scrolls as a side effect.
     */
    scrollToItem(item) {
      if (!this.isHorizontal) {
        item.scrollIntoView({
          block: 'center',
          behavior: prefersReducedMotion() ? 'auto' : 'smooth'
        });
        return;
      }

      var railRect = this.rail.getBoundingClientRect();
      var itemRect = item.getBoundingClientRect();
      var delta = itemRect.left + itemRect.width / 2 - (railRect.left + railRect.width / 2);

      this.rail.scrollTo({
        left: this.rail.scrollLeft + delta,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    updateControls() {
      if (!this.isHorizontal) {
        if (this.prevBtn) this.prevBtn.disabled = true;
        if (this.nextBtn) this.nextBtn.disabled = true;
        if (this.progressBar) this.progressBar.style.transform = 'scaleX(0)';
        return;
      }

      var maxScroll = this.rail.scrollWidth - this.rail.clientWidth;
      var atStart = this.rail.scrollLeft <= 1;
      var atEnd = this.rail.scrollLeft >= maxScroll - 1;

      if (this.prevBtn) this.prevBtn.disabled = atStart;
      if (this.nextBtn) this.nextBtn.disabled = atEnd;

      if (this.progressBar) {
        var ratio = maxScroll > 0 ? this.rail.scrollLeft / maxScroll : 0;
        ratio = Math.max(0, Math.min(ratio, 1));
        // A sliver at rest reads as "start of the timeline" rather than as a
        // broken or empty bar.
        this.progressBar.style.transform = 'scaleX(' + (0.04 + ratio * 0.96).toFixed(4) + ')';
      }
    }

    /* ----------------------------------------------------------------------
       Active milestone + year indicator
       ---------------------------------------------------------------------- */

    observeItems() {
      if (this.observer) this.observer.disconnect();

      // Narrow band across the middle of the scroll region: whichever
      // milestone intersects it is the one being read. This is what the year
      // indicator reports, rather than the old interpolated month counter.
      var options = this.isHorizontal
        ? { root: this.rail, rootMargin: '0px -45% 0px -45%', threshold: 0 }
        : { root: null, rootMargin: '-45% 0px -45% 0px', threshold: 0 };

      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(
            function (entry) {
              if (entry.isIntersecting) this.setActiveItem(entry.target);
            }.bind(this)
          );
        }.bind(this),
        options
      );

      this.items.forEach(
        function (item) {
          this.observer.observe(item);
        }.bind(this)
      );
    }

    setActiveItem(item) {
      if (!item || item === this.activeItem) return;

      if (this.activeItem) this.activeItem.classList.remove('is-active');
      item.classList.add('is-active');
      this.activeItem = item;

      if (this.yearEl) this.yearEl.textContent = item.dataset.year || '';
      if (this.dateEl) this.dateEl.textContent = item.dataset.date || '';
    }

    /* ----------------------------------------------------------------------
       Breakpoint + theme editor
       ---------------------------------------------------------------------- */

    bindBreakpoint() {
      var handler = function () {
        var next = this.railIsHorizontal();
        if (next === this.isHorizontal) return;

        this.isHorizontal = next;
        this.dragging = false;
        this.dragIntent = false;
        this.classList.remove('is-dragging');

        this.updateTabIndex();
        this.observeItems();
        this.updateControls();
      }.bind(this);

      if (this.desktopQuery.addEventListener) {
        this.desktopQuery.addEventListener('change', handler, { signal: this.signal });
      } else {
        // Safari < 14
        this.desktopQuery.addListener(handler);
      }
    }

    bindThemeEditor() {
      if (!window.Shopify || !window.Shopify.designMode) return;

      document.addEventListener(
        'shopify:block:select',
        function (event) {
          if (!this.contains(event.target)) return;
          this.setActiveItem(event.target);
          this.scrollToItem(event.target);
        }.bind(this),
        { signal: this.signal }
      );
    }
  }

  if (!customElements.get('about-timeline')) {
    customElements.define('about-timeline', AboutTimeline);
  }
})();
